import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import '@/App.css';
import { machineApi } from './api/machine';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Layout components
import DockLayout from './components/layout/DockLayout';
import CommandBar from './components/layout/CommandBar';
import WorkspaceDock from './components/layout/WorkspaceDock';
import CenterSurface from './components/layout/CenterSurface';
import ContextDock from './components/layout/ContextDock';
import CommandPalette from './components/CommandPalette';
import ImportMachineDialog from './components/ImportMachineDialog';
import CreateMachineDialog from './components/CreateMachineDialog';
import QuickStartTutorial from './components/QuickStartTutorial';
import TemplateGallery from './components/TemplateGallery';
import GlobalSearch from './components/GlobalSearch';
import ShareDialog from './components/ShareDialog';
import CommandMode from './components/CommandMode';
import { KeyboardShortcuts } from './components/InlineHelp';
import AIGenerator from './components/AIGenerator';
import CollaborationPresence from './components/CollaborationPresence';

// Auth components
import { AuthProvider } from './components/auth/AuthContext';
import AuthCallback from './components/auth/AuthCallback';
import LoginButton from './components/auth/LoginButton';

// Pricing components
import PricingPage from './components/pricing/PricingPage';
import SubscriptionSuccess from './components/pricing/SubscriptionSuccess';
import SubscriptionCancel from './components/pricing/SubscriptionCancel';

// State management
import { UIPanelStateMachine, UIEvents } from './state/UIStateMachine';

import { Button } from './components/ui/button';
import { Layers, Sparkles } from 'lucide-react';


function App() {
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [currentMachine, setCurrentMachine] = useState(null);
  const [validation, setValidation] = useState(null);
  const [capabilities, setCapabilities] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [simulationTrace, setSimulationTrace] = useState([]);
  const centerSurfaceRef = useRef(null);
  
  // UI State Machine
  const [uiStateMachine] = useState(() => {
    const sm = new UIPanelStateMachine();
    sm.restore();
    return sm;
  });
  const [uiState, setUiState] = useState(uiStateMachine.state);
  
  // Layout state
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [templateGalleryOpen, setTemplateGalleryOpen] = useState(false);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [commandModeOpen, setCommandModeOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadMachines();
    loadCapabilities();
    
    // Global keyboard shortcuts
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + K for command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(true);
      }
      
      // Cmd/Ctrl + F for global search
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setGlobalSearchOpen(true);
      }
      
      // : for command mode (vim-style)
      if (e.key === ':' && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        setCommandModeOpen(true);
      }
      
      // ? for shortcuts help
      if (e.key === '?' && !['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        e.preventDefault();
        setShortcutsOpen(true);
      }
      
      // Esc to close modals/focus mode
      if (e.key === 'Escape') {
        if (uiState.focusMode) {
          dispatchUIEvent(UIEvents.EXIT_FOCUS);
        }
      }
      
      // Panel shortcuts (when not in input)
      if (!['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
        // W - Toggle workspace (left panel)
        if (e.key === 'w' || e.key === 'W') {
          e.preventDefault();
          dispatchUIEvent(UIEvents.TOGGLE_LEFT);
        }
        // I - Toggle inspector (right panel)
        if (e.key === 'i' || e.key === 'I') {
          e.preventDefault();
          dispatchUIEvent(UIEvents.TOGGLE_RIGHT);
        }
        // F - Toggle focus mode
        if (e.key === 'f' || e.key === 'F') {
          e.preventDefault();
          dispatchUIEvent(uiState.focusMode ? UIEvents.EXIT_FOCUS : UIEvents.ENTER_FOCUS);
        }
        // 0 - Open all panels
        if (e.key === '0') {
          e.preventDefault();
          dispatchUIEvent(UIEvents.OPEN_ALL);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uiState.focusMode]);

  useEffect(() => {
    if (selectedMachine) {
      loadMachine(selectedMachine);
    }
  }, [selectedMachine]);

  const dispatchUIEvent = (event, payload) => {
    const { newState } = uiStateMachine.dispatch(event, payload);
    setUiState(newState);
    uiStateMachine.persist();
  };

  const loadMachines = async () => {
    try {
      const data = await machineApi.listMachines();
      setMachines(data);
      
      // Auto-select first machine if none selected
      if (!selectedMachine && data.length > 0) {
        setSelectedMachine(data[0].machine_id);
      }
    } catch (error) {
      console.error('Failed to load machines:', error);
      toast.error('Failed to load machines');
    }
  };

  const loadCapabilities = async () => {
    try {
      const data = await machineApi.getCapabilities();
      setCapabilities(data);
    } catch (error) {
      console.error('Failed to load capabilities:', error);
    }
  };

  const loadMachine = async (machineId) => {
    try {
      const data = await machineApi.getMachine(machineId);
      setCurrentMachine(data);
      
      // Auto-validate
      const validationResult = await machineApi.validateMachine(machineId);
      setValidation(validationResult);
    } catch (error) {
      toast.error('Failed to load machine');
      console.error(error);
    }
  };

  const handleRevalidate = async () => {
    if (!selectedMachine) return;
    try {
      const validationResult = await machineApi.validateMachine(selectedMachine);
      setValidation(validationResult);
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteMachine = async (machineId) => {
    try {
      await machineApi.deleteMachine(machineId);
      toast.success('Machine deleted');
      if (selectedMachine === machineId) {
        setSelectedMachine(null);
        setCurrentMachine(null);
        setValidation(null);
      }
      await loadMachines();
    } catch (error) {
      toast.error('Failed to delete machine');
    }
  };


  // Persist machine updates from visual editing
  const handleMachineUpdate = async (updatedMachineIR) => {
    if (!selectedMachine) return;
    
    try {
      // Update local state immediately for responsiveness
      setCurrentMachine(prev => ({
        ...prev,
        machine_ir: updatedMachineIR
      }));

      const persistedMachine = await machineApi.updateMachine(selectedMachine, updatedMachineIR);
      setCurrentMachine(persistedMachine);
      await loadMachines();

      if (selectedNode && !persistedMachine.machine_ir?.states?.[selectedNode]) {
        setSelectedNode(null);
      }

      const validationResult = await machineApi.validateMachine(selectedMachine);
      setValidation(validationResult);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save machine changes');
      console.error(error);
      throw error;
    }
  };

  // Handle DSL text edits
  const handleDSLEdit = async (dslText) => {
    if (!selectedMachine) return;

    try {
      const updatedMachine = await machineApi.updateMachine(selectedMachine, dslText);
      setCurrentMachine(updatedMachine);
      await loadMachines();

      if (selectedNode && !updatedMachine.machine_ir?.states?.[selectedNode]) {
        setSelectedNode(null);
      }

      const validationResult = await machineApi.validateMachine(selectedMachine);
      setValidation(validationResult);
      toast.success('DSL changes applied');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse DSL');
      throw error;
    }
  };

  // Unified command handler for command palette and command mode
  const handleCommand = async (commandId, param) => {
    switch (commandId) {
      case 'create':
        setCreateDialogOpen(true);
        return true;
      case 'ai-generate':
        setAiGeneratorOpen(true);
        return true;
      case 'import':
      case 'import-dsl':
        setImportDialogOpen(true);
        return true;
      case 'open-caps-effects':
        toast.info('Switch to "Caps & Effects" tab in center panel');
        return true;
      case 'add-effect':
        toast.info('Switch to "Caps & Effects" tab to add effects');
        return true;
      case 'graph-view':
      case 'view-graph':
        toast.info('Switch to "Graph" tab in center panel');
        return true;
      case 'view-dsl':
        toast.info('Switch to "DSL" tab in center panel');
        return true;
      case 'view-caps':
        toast.info('Switch to "Caps & Effects" tab');
        return true;
      case 'validate':
        if (selectedMachine) {
          await handleRevalidate();
          toast.success('Validation complete');
          return true;
        }
        toast.error('Select a machine first');
        return false;
      case 'simulate':
      case 'sim-start':
      case 'sim-stop':
      case 'sim-reset':
        toast.info('Use Simulation panel to step through execution');
        return true;
      case 'export-ts':
      case 'export-schema':
      case 'export-json':
      case 'export-graph':
        toast.info('Use Export panel to compile and download');
        return true;
      case 'toggle-inspector':
        dispatchUIEvent(UIEvents.TOGGLE_RIGHT);
        return true;
      case 'toggle-workspace':
        dispatchUIEvent(UIEvents.TOGGLE_LEFT);
        return true;
      case 'focus-mode':
      case 'focus':
        dispatchUIEvent(uiState.focusMode ? UIEvents.EXIT_FOCUS : UIEvents.ENTER_FOCUS);
        return true;
      case 'save':
        if (currentMachine?.machine_ir && selectedMachine) {
          await machineApi.updateMachine(selectedMachine, currentMachine.machine_ir);
          await loadMachines();
          toast.success('Machine saved');
          return true;
        }
        toast.error('No machine to save');
        return false;
      case 'share':
        if (selectedMachine) {
          setShareDialogOpen(true);
          return true;
        }
        toast.error('Select a machine first');
        return false;
      case 'help':
        toast.info('Commands: :new, :save, :share, :focus, :export ts, :sim start');
        return true;
      case 'shortcuts':
        setShortcutsOpen(true);
        return true;
      case 'close':
        setCommandModeOpen(false);
        return true;
      case 'goto':
        if (param && currentMachine?.machine_ir?.states?.[param]) {
          setSelectedNode(param);
          toast.success(`Jumped to ${param}`);
          return true;
        }
        toast.error(`State "${param}" not found`);
        return false;
      default:
        return false;
    }
  };

  // NEW: Minimal machine creation from IR (no DSL required)
  const handleCreateMachine = async (machineIR) => {
    setIsCreating(true);
    try {
      // Convert minimal IR to DSL for backend
      const dslSource = generateMinimalDSL(machineIR);
      const result = await machineApi.createMachine({ dsl_source: dslSource });
      
      toast.success(`Machine "${machineIR.name}" created`);
      setCreateDialogOpen(false);
      
      // Reload machines and select the new one
      await loadMachines();
      setSelectedMachine(result.machine_id);
      
      // Select the initial state node
      setSelectedNode(machineIR.initial);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create machine');
    } finally {
      setIsCreating(false);
    }
  };

  // Generate minimal DSL from machine IR
  const generateMinimalDSL = (machineIR) => {
    const lines = [`machine ${machineIR.name} {`];
    // Declare initial state
    lines.push(`  initial state ${machineIR.initial}`);
    
    // Define all states with bodies
    Object.keys(machineIR.states).forEach(stateId => {
      lines.push(`  state ${stateId} {}`);
    });
    
    lines.push('}');
    return lines.join('\n');
  };

  // Handle template selection - creates machine from template IR
  const handleSelectTemplate = async (templateIR) => {
    setIsCreating(true);
    try {
      // Generate unique machine ID
      const machineId = `machine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Build complete IR with required fields
      const completeIR = {
        id: machineId,
        name: templateIR.name,
        version: {
          version: "1.0.0",
          created_at: new Date().toISOString(),
          created_by: null,
          parent_hash: null,
          migration_applied: null
        },
        initial: templateIR.initial,
        context_type: "Record<string, any>",
        states: Object.fromEntries(
          Object.entries(templateIR.states).map(([key, state]) => [
            key,
            {
              id: state.id,
              type: state.type || 'atomic',
              initial: state.initial || null,
              states: state.states || {},
              transitions: (state.transitions || []).map(t => ({
                event: t.event,
                target: t.target,
                guards: (t.guards || []).map(g => 
                  typeof g === 'string' 
                    ? { name: g, predicate: g, params: {} }
                    : g
                ),
                effects: t.effects || []
              })),
              entry: state.entry || [],
              exit: state.exit || [],
              invariants: (state.invariants || []).map(inv =>
                typeof inv === 'string'
                  ? { name: inv, assertion: inv, message: inv }
                  : inv
              ),
              meta: state.meta || {}
            }
          ])
        ),
        events: templateIR.events || [],
        effects: templateIR.effects || {},
        guards: templateIR.guards || {},
        invariants: templateIR.invariants || [],
        meta: templateIR.meta || {}
      };
      
      // Send the complete IR to backend
      const result = await machineApi.createMachine({ ir_json: completeIR });
      
      toast.success(`Machine "${templateIR.name}" created from template`);
      setTemplateGalleryOpen(false);
      
      // Reload machines and select the new one
      await loadMachines();
      setSelectedMachine(result.machine_id);
      
      // Select the initial state node
      setSelectedNode(templateIR.initial);
    } catch (error) {
      console.error('Template creation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create machine from template');
    } finally {
      setIsCreating(false);
    }
  };

  const machineStatus = validation?.valid ? 'valid' : validation ? 'invalid' : 'unknown';

  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Auth routes */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
            
            {/* Main app */}
            <Route
              path="/"
              element={
                <DockLayout
                  focusMode={uiState.focusMode}
                  leftDockOpen={uiState.leftPanelOpen}
                  rightDockOpen={uiState.rightPanelOpen}
                  onLeftDockToggle={() => dispatchUIEvent(UIEvents.TOGGLE_LEFT)}
                  onRightDockToggle={() => dispatchUIEvent(UIEvents.TOGGLE_RIGHT)}
                  topBar={
                    <CommandBar
                      machineName={currentMachine?.name}
                      machineHash={currentMachine?.version_hash}
                      machineStatus={machineStatus}
                      syncStatus="synced"
                      onCommandPaletteOpen={() => setCommandPaletteOpen(true)}
                      onRefresh={loadMachines}
                    />
                  }
                  loginButton={<LoginButton />}
                  leftDock={
                    <WorkspaceDock
                      machines={machines}
                      selectedMachine={selectedMachine}
                      onSelectMachine={setSelectedMachine}
                      onNewMachine={() => setCreateDialogOpen(true)}
                      onImportMachine={() => setImportDialogOpen(true)}
                      onOpenTemplates={() => setTemplateGalleryOpen(true)}
                      onOpenAIGenerator={() => setAiGeneratorOpen(true)}
                      onDeleteMachine={handleDeleteMachine}
                    />
                  }
                centerSurface={
                  selectedMachine && currentMachine ? (
                    <CenterSurface
                      machine={currentMachine.machine_ir}
                      machineId={selectedMachine}
                      validation={validation}
                      capabilities={capabilities}
                      onNodeSelect={setSelectedNode}
                      onRevalidate={handleRevalidate}
                      onMachineUpdate={handleMachineUpdate}
                      onDSLEdit={handleDSLEdit}
                      focusMode={uiState.focusMode}
                      onFocusModeToggle={() => dispatchUIEvent(uiState.focusMode ? UIEvents.EXIT_FOCUS : UIEvents.ENTER_FOCUS)}
                      onSimulationTraceUpdate={setSimulationTrace}
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <div className="text-center space-y-6 max-w-lg px-4">
                        <img 
                          src="/firmastate-splash.png" 
                          alt="FIRMASTATE - State Machine & Graph Based IDE" 
                          className="mx-auto max-w-md w-full opacity-90"
                          data-testid="splash-image"
                        />
                        <p className="text-sm text-gray-400">
                          A meta-computational substrate for authoring, validating,
                          simulating, and compiling state machines with capability-typed effects.
                        </p>
                        <div className="flex items-center justify-center gap-3 pt-2">
                          <Button 
                            onClick={() => setCreateDialogOpen(true)}
                            data-testid="empty-create-btn"
                          >
                            Create Machine
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setTemplateGalleryOpen(true)}
                            data-testid="empty-templates-btn"
                          >
                            <Layers className="w-4 h-4 mr-2" />
                            Templates
                          </Button>
                          <Button 
                            variant="outline"
                            onClick={() => setAiGeneratorOpen(true)}
                            data-testid="empty-ai-btn"
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            AI
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                }
                rightDock={
                  <ContextDock
                    machine={currentMachine?.machine_ir}
                    selectedNode={selectedNode}
                    validation={validation}
                    capabilities={capabilities}
                    machineId={selectedMachine}
                    onRevalidate={handleRevalidate}
                    simulationTrace={simulationTrace}
                  />
                }
              />
            }
          />
        </Routes>
      </BrowserRouter>

      {/* Command Palette */}
      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        onCommand={handleCommand}
      />

      {/* Create Machine Dialog (Minimal, Graph-First) */}
      <CreateMachineDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onCreate={handleCreateMachine}
        isCreating={isCreating}
      />

      {/* Import Machine Dialog (DSL-based, Advanced) */}
      <ImportMachineDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImportSuccess={(machine) => {
          loadMachines();
          setSelectedMachine(machine.machine_id);
        }}
      />

      {/* Template Gallery */}
      <TemplateGallery
        open={templateGalleryOpen}
        onOpenChange={setTemplateGalleryOpen}
        onSelectTemplate={handleSelectTemplate}
        isCreating={isCreating}
      />

      {/* Global Search */}
      <GlobalSearch
        open={globalSearchOpen}
        onOpenChange={setGlobalSearchOpen}
        machines={machines.map(m => ({
          machine_id: m.machine_id,
          name: m.name,
          states: m.machine_ir?.states || {},
          effects: m.machine_ir?.effects || {}
        }))}
        onSelectMachine={setSelectedMachine}
        onSelectState={setSelectedNode}
      />

      {/* Share Dialog */}
      <ShareDialog
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        machineId={selectedMachine}
        machineName={currentMachine?.name || 'Untitled'}
      />

      {/* AI Generator */}
      <AIGenerator
        open={aiGeneratorOpen}
        onOpenChange={setAiGeneratorOpen}
        onMachineGenerated={async (machineIR) => {
          try {
            const result = await machineApi.createMachine({ ir_json: machineIR });
            toast.success(`AI machine "${machineIR.name}" created`);
            await loadMachines();
            setSelectedMachine(result.machine_id);
          } catch (error) {
            toast.error('Failed to create AI-generated machine');
          }
        }}
      />

      {/* Command Mode */}
      <CommandMode
        open={commandModeOpen}
        onClose={() => setCommandModeOpen(false)}
        onCommand={handleCommand}
      />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcuts
        open={shortcutsOpen}
        onOpenChange={setShortcutsOpen}
      />

      {/* Quick Start Tutorial for new users */}
      <QuickStartTutorial 
        onComplete={() => toast.success('Tutorial complete! Start building.')}
      />

      <Toaster position="bottom-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
